import React, { useMemo } from 'react';
import { ChevronRight, Check, Layers, Plus, Sparkles, TriangleAlert } from 'lucide-react';
import { KIND_ICON, KIND_LABEL, REORG_LABEL, endingSentence, plural, treeOf } from '../format';
import { headOf } from '../chart';
import type { GovNodeView, GovOfficeHolder } from '../types';

import { foldText } from '../../../utils/fuzzySearch';
export type TreeFilter = 'all' | 'review' | 'merge' | 'new' | 'applied' | 'changes' | 'attention' | 'defunct';

export const FILTERS: Array<{ value: TreeFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'review', label: 'To review' },
  { value: 'merge', label: 'To merge' },
  { value: 'new', label: 'New' },
  { value: 'applied', label: 'In directory' },
  { value: 'changes', label: 'Changes waiting' },
  { value: 'attention', label: 'Needs a look' },
  { value: 'defunct', label: 'Defunct' },
];

export const matchesFilter = (
  node: GovNodeView,
  filter: TreeFilter,
  pendingByOrg?: Map<string, number>,
): boolean => {
  // The structure is the state as it is: a body that ended shows only when
  // asked for (the timeline shows it under the body that replaced it)
  if (node.ended && filter !== 'defunct') return false;
  switch (filter) {
    case 'defunct':
      return !!node.ended;
    case 'changes':
      return !!node.organizationId && (pendingByOrg?.get(node.organizationId) ?? 0) > 0;
    case 'review':
      return node.status === 'proposed';
    case 'merge':
      return node.decision.action === 'merge';
    case 'new':
      return node.status !== 'applied' && node.decision.action === 'create';
    case 'applied':
      return node.status === 'applied';
    case 'attention':
      return (node.stale && !node.ended) || node.confidence < 55 || node.status === 'rejected';
    case 'all':
    default:
      return node.status !== 'rejected';
  }
};

const statusMark = (node: GovNodeView) => {
  if (node.status === 'applied') {
    return (
      <span
        title={`In the directory as “${node.organization?.label ?? 'a record'}”`}
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"
      >
        <Check className="h-3 w-3" strokeWidth={2.25} />
      </span>
    );
  }
  if (node.status === 'confirmed') {
    return (
      <span
        title="Confirmed, ready to apply"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-neutral-100 text-neutral-500"
      >
        <Check className="h-3 w-3" strokeWidth={2} />
      </span>
    );
  }
  return (
    <span
      title="Found by research, not reviewed yet"
      className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-50 text-amber-600"
    >
      <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
    </span>
  );
};

const Row: React.FC<{
  node: GovNodeView;
  depth: number;
  childCount: number;
  expanded: boolean;
  selected: boolean;
  holders: GovOfficeHolder[];
  waiting: number;
  changed: boolean;
  onToggle: () => void;
  onSelect: () => void;
}> = ({ node, depth, childCount, expanded, selected, holders, waiting, changed, onToggle, onSelect }) => {
  const Icon = KIND_ICON[node.kind];
  const merges = node.decision.action === 'merge' ? (node.decision.mergeIds?.length ?? 0) + 1 : 0;
  // Who runs it, as the directory has it; research's name only where the
  // directory has nobody, and marked as such
  const head = headOf(node, holders);

  return (
    <div
      className={`group relative flex min-h-[44px] w-full items-center gap-1.5 pr-2 transition-colors duration-700 sm:min-h-[38px] ${
        selected ? 'bg-primary-50/70' : changed ? 'bg-amber-50' : 'hover:bg-neutral-50'
      }`}
      style={{ paddingLeft: 8 + depth * 16 }}
      data-changed={changed || undefined}
    >
      {depth > 0 && (
        <span
          aria-hidden="true"
          className="absolute inset-y-0 w-px bg-neutral-200/80"
          style={{ left: depth * 16 - 2 }}
        />
      )}
      <button
        type="button"
        onClick={onToggle}
        aria-label={childCount ? (expanded ? 'Collapse' : 'Expand') : undefined}
        tabIndex={childCount ? 0 : -1}
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded text-neutral-400 sm:h-5 sm:w-5 ${
          childCount ? 'hover:bg-neutral-200/70 hover:text-ink' : 'invisible'
        }`}
      >
        <ChevronRight
          className={`h-3.5 w-3.5 transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
          strokeWidth={2}
        />
      </button>
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left focus-visible:outline-none"
      >
        <Icon
          className={`h-4 w-4 flex-shrink-0 ${selected ? 'text-primary-600' : 'text-neutral-400'}`}
          strokeWidth={1.75}
          aria-label={KIND_LABEL[node.kind]}
        />
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-center gap-1.5">
            <span
              className={`truncate text-[13px] ${
                node.status === 'rejected'
                  ? 'text-neutral-400 line-through'
                  : node.ended
                    ? 'text-neutral-400'
                    : 'text-ink'
              }`}
            >
              {node.name}
            </span>
            {node.acronym && (
              <span className="flex-shrink-0 text-[11.5px] text-neutral-400">{node.acronym}</span>
            )}
          </span>
          {node.ended ? (
            <span className="mt-0.5 block truncate text-[11.5px] text-neutral-400">{endingSentence(node.ended)}</span>
          ) : (
            head && (
              <span
                className="mt-0.5 block truncate text-[11.5px] text-neutral-400"
                title={head.recorded ? 'As the directory records it' : 'Found by research'}
              >
                {head.name}
                {head.role ? ` · ${head.role}` : ''}
                {/* Only an entity with a record can say who its record lacks */}
                {!head.recorded && node.status === 'applied' && (
                  <span className="text-amber-700"> · not recorded</span>
                )}

              </span>
            )
          )}
        </span>
      </button>

      <span className="flex flex-shrink-0 items-center gap-1.5">
        {waiting > 0 && (
          <span
            title={`${plural(waiting, 'change')} waiting for review`}
            className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-violet-50 px-1 text-[10.5px] tabular-nums text-violet-700"
          >
            {waiting}
          </span>
        )}
        {node.ended && (
          <span className="rounded-full bg-neutral-100 px-1.5 text-[11px] text-neutral-500">
            {REORG_LABEL[node.ended.how]}
          </span>
        )}
        {node.stale && !node.ended && (
          <span title="Not found by the latest research" className="text-amber-500">
            <TriangleAlert className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
        )}
        {merges > 0 && (
          <span
            title={`${plural(merges, 'record')} in the directory would become one`}
            className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-1.5 text-[11px] text-teal-700"
          >
            <Layers className="h-3 w-3" strokeWidth={1.75} />
            {merges}
          </span>
        )}
        {node.status !== 'applied' && node.decision.action === 'create' && (
          <span title="Not in the directory yet" className="text-neutral-300">
            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
        )}
        {childCount > 0 && (
          <span className="w-5 text-right text-[11px] tabular-nums text-neutral-300">{childCount}</span>
        )}
        {statusMark(node)}
      </span>
    </div>
  );
};

/**
 * The state as a tree: what research found, where each entity sits, and what
 * would happen to the directory if it were applied.
 */
export const StructureTree: React.FC<{
  nodes: GovNodeView[];
  filter: TreeFilter;
  search: string;
  expanded: Set<string>;
  selectedId: string | null;
  holdersByOrg?: Map<string, GovOfficeHolder[]>;
  pendingByOrg?: Map<string, number>;
  recentlyChanged?: Set<string>;
  onToggle: (id: string) => void;
  onSelect: (id: string) => void;
}> = ({
  nodes,
  filter,
  search,
  expanded,
  selectedId,
  holdersByOrg,
  pendingByOrg,
  recentlyChanged,
  onToggle,
  onSelect,
}) => {
  const { childrenOf, byId } = useMemo(() => treeOf(nodes), [nodes]);

  // A node stays visible when it matches, and so do its ancestors, so the
  // tree never shows a child without the thing it belongs to
  const visible = useMemo(() => {
    // Folded on both sides: typing "pre" finds "Présidence"
    const term = foldText(search);
    const hit = (node: GovNodeView) =>
      matchesFilter(node, filter, pendingByOrg) &&
      (!term ||
        foldText(node.name).includes(term) ||
        foldText(node.nameEn).includes(term) ||
        foldText(node.acronym).includes(term) ||
        foldText(node.head?.name).includes(term) ||
        (node.organizationId
          ? (holdersByOrg?.get(node.organizationId) ?? []).some((holder) => foldText(holder.name).includes(term))
          : false) ||
        node.matches.some((match) => foldText(match.label).includes(term)));

    const keep = new Set<string>();
    for (const node of nodes) {
      if (!hit(node)) continue;
      keep.add(node.id);
      let parentId = node.parentId;
      for (let depth = 0; parentId && depth < 20; depth++) {
        keep.add(parentId);
        parentId = byId.get(parentId)?.parentId ?? null;
      }
    }
    return keep;
  }, [nodes, filter, search, byId, pendingByOrg, holdersByOrg]);

  const term = search.trim();
  const rows: React.ReactNode[] = [];
  const walk = (list: GovNodeView[], depth: number) => {
    for (const node of list) {
      if (!visible.has(node.id)) continue;
      const children = (childrenOf.get(node.id) ?? []).filter((child) => visible.has(child.id));
      // Searching opens the tree; otherwise the reader decides what is open
      const isOpen = expanded.has(node.id) || (!!term && children.length > 0);
      rows.push(
        <Row
          key={node.id}
          node={node}
          depth={depth}
          childCount={children.length}
          expanded={isOpen}
          selected={selectedId === node.id}
          holders={node.organizationId ? (holdersByOrg?.get(node.organizationId) ?? []) : []}
          waiting={node.organizationId ? (pendingByOrg?.get(node.organizationId) ?? 0) : 0}
          changed={!!recentlyChanged?.has(node.id)}
          onToggle={() => onToggle(node.id)}

          onSelect={() => onSelect(node.id)}
        />,
      );
      if (isOpen) walk(children, depth + 1);
    }
  };
  walk(childrenOf.get(null) ?? [], 0);

  if (!rows.length) {
    return (
      <p className="px-4 py-8 text-center text-[13px] text-neutral-400">
        {nodes.length ? 'Nothing matches' : 'Nothing mapped yet'}
      </p>
    );
  }
  return <div className="divide-y divide-neutral-100">{rows}</div>;
};
